/**
 * 운영 인증 신청·서류 API 계약 사본 — 허브 `backend-facecock-was/task-771/outputs/api-spec.md`
 * §2~§4 그대로(`#770` §2 EP-A 응답 shape 을 §2 가 재사용). `venueRegisterTypes.ts`·`venueProfileTypes.ts`
 * 와 같은 규칙 — 서버가 계약을 바꾸지 않는 한 여기서 필드를 파생하거나 이름을 바꾸지 않는다.
 */

/** api-spec §7 — forward-only. */
export type BusinessType = 'INDIVIDUAL' | 'CORPORATION'

export const BUSINESS_TYPE_CATALOG: { value: BusinessType; label: string }[] = [
  { value: 'INDIVIDUAL', label: '개인' },
  { value: 'CORPORATION', label: '법인' },
]

/** 회차(신청) 1건의 진행 상태 — `PENDING`(심사 중) / `APPROVED`(승인) / `REJECTED`(반려). forward-only. */
export type OperationVerificationProgress = 'PENDING' | 'APPROVED' | 'REJECTED'

/** `POST .../operation-verification` 요청 바디(api-spec §2). */
export interface OperationVerificationSubmitInput {
  businessType: BusinessType
  representativeName: string
  businessNumber: string
  contact: string
  operationAddress: string | null
  operationStartedOn: string | null
  /** EP-2 가 준 값 그대로. 서류를 첨부하지 않았으면 `null`(미첨부 제출은 허용된다 — api-spec §2). */
  documentKey: string | null
  /** `documentKey` 를 보내면 필수. */
  documentFileName: string | null
  additionalNote: string | null
}

export interface OperationVerificationRejection {
  reason: string
  rejectedAt: string
}

export interface OperationVerificationLatestApplication {
  verificationId: number
  progress: OperationVerificationProgress
  progressLabel: string
  submittedAt: string
  reviewedAt: string | null
}

/** 구장 노출 차단 사유(#778, 백엔드 #777 §2-1 + #780 §2) — forward-only. */
export type VisibilityBlockReason =
  | 'REVIEW_PENDING'
  | 'REVIEW_REJECTED'
  | 'HIDDEN_BY_OPERATOR'
  | 'OPERATION_UNVERIFIED'
  | 'OPERATION_EXPIRED'
  | 'HIDDEN_BY_VERIFICATION_EXPIRY'

/**
 * 노출 차단 사유 1건의 표시용 뷰 — `visibilityBlockReasons`(문자열 배열, 분기용)와 **같은 순서·같은
 * 길이**로 나란히 내려온다(api-spec §2-1). 🚨 `resolvableByManager=false`인 사유에는 "운영 인증하기"
 * CTA를 붙이지 않는다 — 운영자가 내려 둔 구장은 인증을 다시 마쳐도 돌아오지 않는다.
 */
export interface VisibilityBlockReasonView {
  reason: VisibilityBlockReason | (string & {})
  label: string
  notice: string
  resolvableByManager: boolean
}

/** 인증 주기 정책(#778, 백엔드 #777 §2-4). 정책 미설정이면 `null`(그래도 조회는 200). */
export interface OperationVerificationPolicy {
  cycleUnit: string
  cycleUnitLabel: string
  cycleValue: number
  cycleMonths: number
}

/**
 * `POST .../operation-verification` 응답 200(api-spec §2) — `#770` §2 EP-A(`GET`, 상태 조회)와
 * **완전히 같은 shape**. `#778`이 `GET`(상태 조회 화면)을 소비하며 `#777`이 추가한 4블록(노출 차단
 * 사유·예약 가능 여부·재신청 가능 여부·인증 주기 정책)을 확장했다 — 기존 필드는 한 글자도 바꾸지 않는다.
 */
export interface OperationVerificationView {
  baseTime: string
  venueId: string
  /** 만료 임박 창(일). 🚨 화면이 D-7 을 하드코딩하지 말 것. */
  expiringWithinDays: number

  /** 화면 배지(서버 파생값). forward-only. */
  badge: string
  badgeLabel: string
  verificationStatus: string
  verificationStatusLabel: string
  applicationStatus: string
  applicationStatusLabel: string

  verifiedUntil: string | null
  verifiedAt: string | null
  remainingDays: number | null
  expiringSoon: boolean

  exposureStatus: string
  exposureStatusLabel: string
  userVisible: boolean

  /** 노출 중단 사유(#780 §2) — `exposureStatus==='EXPOSED'`면 항상 `null`(둘은 함께 바뀐다). */
  exposureHiddenReason: 'OPERATOR' | 'VERIFICATION_EXPIRED' | (string & {}) | null
  exposureHiddenReasonLabel: string | null

  /** 최신 회차가 반려일 때만 채워진다. */
  rejection: OperationVerificationRejection | null
  /** 신청한 적 없으면 `null`. */
  latestApplication: OperationVerificationLatestApplication | null

  /** 🚨 빈 배열 ⟺ `userVisible === true`(불변식, api-spec §2-1). */
  visibilityBlockReasons: (VisibilityBlockReason | (string & {}))[]
  visibilityBlockReasonViews: VisibilityBlockReasonView[]

  /** 관리자가 켜고 끄는 예약 기능 토글 값 그대로. */
  reservationEnabled: boolean
  /** 지금 신규 온라인 예약을 받을 수 있는가 = `userVisible ∧ reservationEnabled`(서버 파생, 재계산 금지). */
  newOnlineReservationAvailable: boolean

  /** 🚨 막는 조건은 오직 "심사 중인 회차의 존재" 하나 — 인증이 유효해도 갱신 신청은 허용된다. */
  canReapply: boolean
  reapplyBlockReason: string | null

  policy: OperationVerificationPolicy | null
}

/** 회차 처리 결과 파생값(#778, 백엔드 #777 §3-2) — 저장된 심사 결과가 아니라 시각 비교의 결과. */
export type OperationVerificationOutcome = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED'

/** 회차 처리 주체 유형 — 🔒 운영자 실명은 내려오지 않는다(#777 §3-3). */
export type OperationVerificationReviewerType = 'OPERATOR' | 'SYSTEM'

/**
 * 이력(회차) 목록의 행 1건(api-spec §3). 🚨 `outcome`을 클라이언트가 재계산하지 않는다 — 재인증으로
 * 대체된 옛 회차도 서버가 `EXPIRED`로 내려준다(이 목록은 과거 회차의 기록이다).
 */
export interface OperationVerificationApplicationRow {
  verificationId: number
  outcome: OperationVerificationOutcome | (string & {})
  outcomeLabel: string
  progress: OperationVerificationProgress
  progressLabel: string
  submittedAt: string
  /** 심사 중이면 `null`. */
  reviewedAt: string | null
  /** 반려 사유. 승인·심사중이면 `null`. */
  reason: string | null
  reviewerType: OperationVerificationReviewerType | (string & {}) | null
  reviewerTypeLabel: string | null
  verifiedUntil: string | null
  policyCycleUnit: string | null
  policyCycleUnitLabel: string | null
  policyCycleValue: number | null
  documentAttached: boolean
  /** 표시 전용 — 다운로드 경로에 쓰지 않는다. */
  documentFileName: string | null
}

/** `GET .../operation-verification/applications` 응답 200(api-spec §3) — 커서 페이지네이션. */
export interface OperationVerificationApplicationsPage {
  baseTime: string
  venueId: string
  applications: OperationVerificationApplicationRow[]
  nextExist: boolean
  nextCursor: string | null
}

/** `POST .../operation-verification/documents/upload-url` 요청 바디(api-spec §3). */
export interface OperationVerificationUploadUrlInput {
  fileName: string
  /** 참고값 — 서명·저장에 쓰이지 않는다. */
  contentType: string
  fileSize: number
}

/** 응답 200(api-spec §3). */
export interface OperationVerificationUploadUrlResult {
  documentKey: string
  uploadUrl: string
  expiresAt: string
  /** 🚨 S3 `PUT` 시 이 헤더를 **그대로** 보내야 한다 — 서명에 포함돼 있다(api-spec §3). */
  requiredHeaders: Record<string, string>
  maxFileSizeBytes: number
  allowedExtensionsLabel: string
}

/**
 * `GET .../operation-verification/prefill` 응답 200(api-spec §4) — 재신청 폼 prefill 전용.
 * 🚨 신청 이력이 없으면 `hasPrevious=false` + 나머지 전부 `null`(404 가 아니다).
 * 🚫 서류는 재사용 불가 — `documentFileName` 은 표시 전용이고 key 는 내려오지 않는다.
 */
export interface OperationVerificationPrefill {
  venueId: string
  hasPrevious: boolean
  verificationId: number | null
  progress: OperationVerificationProgress | null
  progressLabel: string | null
  submittedAt: string | null

  businessType: BusinessType | null
  businessTypeLabel: string | null
  representativeName: string | null
  businessNumber: string | null
  contact: string | null
  operationAddress: string | null
  operationStartedOn: string | null
  documentFileName: string | null
  additionalNote: string | null
}
